/* eslint-disable prettier/prettier */
import React from 'react';
import {
	CalendarViewMode,
	MonthRange,
	ScheduleViewMode,
	WeekRange,
} from '../../types/schedule.types';
import { formatMonthRange, formatWeekRange } from '../../utils/dateUtils';
import CalendarPopup from './CalendarPopup';
import { useEventDots } from '../../hooks/useEventDots';
import styles from '../../styles/scheduler/WeekNavigator.module.css';

/**
 * Replaces WeekNavigator.
 *
 * This is now a fully CONTROLLED component: it renders the scheduler state
 * owned by ScheduleCalendar (via useScheduler) and calls back up. It no
 * longer instantiates its own useScheduler().
 *
 * Why: the old WeekNavigator held a second copy of the selection and its
 * onPrev/onNext useCallbacks computed the next week from a stale closure
 * (`state.selectedWeek`), then *also* mutated the internal copy. The two
 * copies drifted (rapid clicks, popup selection, or a parent default that
 * differed from the hook's default) and the next arrow click navigated
 * from the internal week instead of the displayed one — the "skipped
 * week" bug. It also used `require()` inside callbacks, which breaks under
 * ESM bundlers. Both are gone: one state, one owner.
 */
interface PeriodNavigatorProps {
	viewMode: ScheduleViewMode;
	selectedWeek: WeekRange;
	selectedMonth: MonthRange;
	currentMonth: number;
	currentYear: number;
	isCalendarOpen: boolean;
	calendarView: CalendarViewMode;
	onPrev: () => void;
	onNext: () => void;
	onOpenCalendar: () => void;
	onCloseCalendar: () => void;
	onSelectWeek: (week: WeekRange) => void;
	onSelectMonthRange: (month: MonthRange) => void;
	onNavigateMonth: (dir: 'prev' | 'next') => void;
	onSetCalendarView: (view: CalendarViewMode) => void;
	onSelectMonth: (month: number) => void;
	onSelectYear: (year: number) => void;
}

const PeriodNavigator: React.FC<PeriodNavigatorProps> = ({
	viewMode,
	selectedWeek,
	selectedMonth,
	currentMonth,
	currentYear,
	isCalendarOpen,
	calendarView,
	onPrev,
	onNext,
	onOpenCalendar,
	onCloseCalendar,
	onSelectWeek,
	onSelectMonthRange,
	onNavigateMonth,
	onSetCalendarView,
	onSelectMonth,
	onSelectYear,
}) => {
	const { eventDots } = useEventDots(currentYear, currentMonth);

	const label =
		viewMode === 'week'
			? formatWeekRange(selectedWeek)
			: formatMonthRange(selectedMonth);

	const unit = viewMode === 'week' ? 'week' : 'month';

	return (
		<div className={styles.wrapper}>
			<div className={styles.bar}>
				<button
					className={styles.nav}
					onClick={onPrev}
					aria-label={`Previous ${unit}`}
				>
					‹
				</button>
				<button
					className={`${styles.label} ${isCalendarOpen ? styles.labelOpen : ''}`}
					onClick={isCalendarOpen ? onCloseCalendar : onOpenCalendar}
					aria-expanded={isCalendarOpen}
				>
					{label}
				</button>
				<button
					className={styles.nav}
					onClick={onNext}
					aria-label={`Next ${unit}`}
				>
					›
				</button>
			</div>

			<CalendarPopup
				isOpen={isCalendarOpen}
				viewMode={viewMode}
				month={currentMonth}
				year={currentYear}
				calendarView={calendarView}
				selectedWeek={selectedWeek}
				selectedMonth={selectedMonth}
				eventDots={eventDots}
				onClose={onCloseCalendar}
				onSelectWeek={onSelectWeek}
				onSelectMonthRange={onSelectMonthRange}
				onNavigateMonth={onNavigateMonth}
				onMonthClick={() =>
					onSetCalendarView(
						calendarView === 'month' ? 'week' : 'month'
					)
				}
				onYearClick={() =>
					onSetCalendarView(calendarView === 'year' ? 'week' : 'year')
				}
				onSelectMonth={onSelectMonth}
				onSelectYear={onSelectYear}
			/>
		</div>
	);
};

export default PeriodNavigator;
